import React, { useState, useEffect } from "react";
import { handleAPIRequest } from "../../../api/api";
import { useSelector } from "react-redux";

const TaxCalculator = () => {
  const [salary, setSalary] = useState("");
  const [category, setCategory] = useState("supplies");
  const [isFiler, setIsFiler] = useState(null);
  const [taxRates, setTaxRates] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [totalSalary, setTotalSalary] = useState(0);
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await handleAPIRequest("get", "tax-rates", null, null, token);
        setTaxRates(response);
      } catch (error) {
        console.error("Error fetching tax rates:", error);
      }
    };
    fetchRates();
  }, [token]);

  const formatNumber = (value) => {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleSalaryChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (!isNaN(rawValue) && rawValue !== "") {
      setSalary(formatNumber(rawValue));
      calculateTax(parseFloat(rawValue));
    } else {
      setSalary("");
      setTaxAmount(0);
      setTotalSalary(0);

    }
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSalary(""); // Reset salary
    setIsFiler(null); // Reset filer status
    setTaxAmount(0); // Reset tax amount
    setTaxRate(0); // Reset tax rate
    setTotalSalary(0);

  };

  const handleFilerChange = (e) => {
    setIsFiler(e.target.value === "filer");
    calculateTax(parseFloat(salary.replace(/,/g, "")));
  };

  useEffect(() => {
    if (salary && isFiler !== null) {
        calculateTax(parseFloat(salary.replace(/,/g, "")));
    }
}, [isFiler, salary, category]); 

  const calculateTax = (inputSalary) => {
    if (!inputSalary || inputSalary <= 0 || isFiler === null) {
        setTaxAmount(0);
        setTotalSalary(0);
        return;
    }

    const taxRateData = taxRates.find((t) => t.category === category);
    if (taxRateData) {
        // Convert tax rates to numbers
        const filerRate = parseFloat(taxRateData.filer_rate);
        const nonFilerRate = parseFloat(taxRateData.non_filer_rate);

        // Apply the correct rate
        const rate = isFiler ? filerRate : nonFilerRate;
        const calculatedTax = inputSalary * rate;

        setTaxRate(rate * 100); // Convert to percentage
        setTaxAmount(calculatedTax);
        setTotalSalary(inputSalary - calculatedTax);
    }
};


  return (
    <div className="w-full max-w-7xl mx-auto bg-white p-4 sm:p-6 rounded-lg shadow-md">
      <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-800 text-center">
        Tax Calculator
      </h2>
      <p className="text-gray-600 text-center text-sm mb-4">
        Calculate your withholding tax based on your salary and category.
      </p>

      <div className="space-y-4">
        {/* Salary Input */}
        <div>
          <label className="block text-gray-700 font-medium mb-1">Enter Amount (PKR):</label>
          <input
            type="text"
            value={salary}
            onChange={handleSalaryChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Enter amount"
            disabled={isFiler === null} // Disable input until filer status is selected
          />
        </div>

        {/* Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Category Select */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Select Category:</label>
            <select
              value={category}
              onChange={handleCategoryChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="supplies">Supplies</option>
              <option value="works">Works</option>
              <option value="services">Services</option>
              <option value="it">IT Equipment</option>
            </select>
          </div>

          {/* Filer Status Select */}
          <div>
            <label className="block text-gray-700 font-medium mb-1">Filer Status:</label>
            <select
              value={isFiler === null ? "" : isFiler ? "filer" : "non-filer"}
              onChange={handleFilerChange}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="" disabled>Select status</option>
              <option value="filer">Filer</option>
              <option value="non-filer">Non-Filer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tax Calculation Summary */}
      {salary && isFiler !== null && (
        <div className="mt-4 p-3 bg-gray-100 rounded-md text-sm">
          <h3 className="text-gray-800 font-semibold mb-2">Tax Summary:</h3>
          <p className="text-gray-700">💰 Amount: <span className="font-semibold">PKR {salary}</span></p>
          <p className="text-gray-700">📂 Category: <span className="font-semibold">{category.charAt(0).toUpperCase() + category.slice(1)}</span></p>
          <p className="text-gray-700">👤 Filer Status: <span className="font-semibold">{isFiler ? "Filer" : "Non-Filer"}</span></p>
          <p className="text-gray-700">📊 Tax Rate: <span className="font-semibold">{taxRate}%</span></p>
          <p className="text-lg font-semibold text-blue-600 mt-2">🔹 Total Tax: PKR {taxAmount.toLocaleString()}</p>
          <p className="text-lg font-semibold text-green-600 mt-2">💸 Total Amount After Tax: PKR {totalSalary.toLocaleString()}</p>
        </div>
      )}
    </div>
  );
};

export default TaxCalculator;
